<template>
    <v-app id="app">
        <v-layout class="layout-main">
            <v-navigation-drawer
            class="nav-main"
            clipped
            absolute
            permanent
            touchless
            stateless
            id="navDrawer"
            :mini-variant="!expandDrawer"
            style="z-index:4;"
            >
                <v-list>
                    <v-tooltip
                    :key="i"
                    v-for="(item, i) in items"
                    right
                    :id="`nav-drawer-${i}`"
                    :disabled="expandDrawer">
                        <v-list-tile
                        router
                        :to="item.to"
                        exact
                        slot="activator">
                            <v-list-tile-action>
                                <component
                                class="nav-icon icon-svg-inline"
                                :color="iconColor(item)"
                                :is="item.iconComponent" />
                            </v-list-tile-action>
                            <v-list-tile-content>
                                <v-list-tile-title v-text="item.title"></v-list-tile-title>
                            </v-list-tile-content>
                        </v-list-tile>
                        <span v-text="item.title"></span>
                    </v-tooltip>
                </v-list>
            </v-navigation-drawer>
            <v-toolbar class="primary ontop" dark fixed clipped-left app>
                <v-tooltip class="ml-0" bottom>
                    <v-toolbar-side-icon
                    id="navigationToggle"
                    slot="activator"
                    @click="toggle">
                    </v-toolbar-side-icon>
                    <span v-if="expandDrawer">Collapse navigation menu</span>
                    <span v-else>Expand navigation menu</span>
                </v-tooltip>
                <v-toolbar-title class="toolbar-route-header" v-text="routeHeader"></v-toolbar-title>
                <v-spacer />
                <v-progress-circular
                v-show="loading"
                class="mr-2"
                indeterminate
                color="accent"
                />
                <search-bar />
                <sidebar-toggle id="sidebar-toggle" v-if="mapRoute" />
            </v-toolbar>
            <v-content class="content-wrap">
                <toast-container />
                <v-container fluid class="page" :class="[routeName, { 'map-page': mapRoute }]">
                    <router-view />
                </v-container>
            </v-content>
            <alerts-container />
            <div id="tourActivator">
                <v-tooltip right>
                    <v-btn
                        slot="activator"
                        color="primary"
                        fab
                        hover
                        @click="showTour = true"
                    >
                        <v-icon>help</v-icon>
                    </v-btn>
                    <span>Show website tour</span>
                </v-tooltip>
            </div>
            <report-interstitial />
            <transition name="fade">
                <div
                v-if="mapRoute && !mapSourceLoaded"
                class="map-loading-scrim">
                    <span class="display-2 white--text mb-1">Loading</span>
                    <div class="loading-wrap">
                        <v-progress-linear color="accent" :indeterminate="true"></v-progress-linear>
                    </div>
                </div>
            </transition>
            <disclaimer-modal v-model="showDisclaimer" />
        </v-layout>
        <tour name="websiteTour" v-model="showTour"></tour>
    </v-app>
</template>

<script>
import AlertsContainer from '@/components/Alerts';
import ToastContainer from '@/components/Toast';
import SidebarToggle from '@/components/SidebarToggle';
import SearchBar from '@/components/Search';
import mapMixin from '@/mixins/map';
import * as appColors from '@/functions/appColors';
import iconWatershed from '@/components/icons/Watershed';
import iconStreamflow from '@/components/icons/Streamflow';
import iconSwQuality from '@/components/icons/SwQuality';
import iconGwQuality from '@/components/icons/GwQuality';
import iconGwLevel from '@/components/icons/GwLevel';
import iconClimate from '@/components/icons/Climate';
import ReportInterstitial from '@/components/ReportInterstitial';
import DisclaimerModal from '@/components/DisclaimerModal';
import Tour from '@/components/Tour';
import { SHOW_NAV_DRAWER } from '@/constants/actions';

export default {
    name: 'app',
    mixins: [
        mapMixin,
    ],
    components: {
        AlertsContainer,
        ToastContainer,
        SidebarToggle,
        SearchBar,
        ReportInterstitial,
        DisclaimerModal,
        Tour,
    },
    data() {
        return {
            items: [
                { iconComponent: iconWatershed, title: 'Watershed Reporting', to: '/watershed' },
                { iconComponent: iconStreamflow, title: 'Streamflow & Water Levels', to: '/streamflow' },
                { iconComponent: iconSwQuality, title: 'Surface Water Quality', to: '/surface-water-quality' },
                { iconComponent: iconGwQuality, title: 'Groundwater Quality', to: '/groundwater-quality' },
                { iconComponent: iconGwLevel, title: 'Groundwater Level', to: '/groundwater-level' },
                { iconComponent: iconClimate, title: 'Climate', to: '/climate' },
            ],
            showDisclaimer: true,
            showTour: localStorage.getItem('ShowTour') !== 'false',
        };
    },
    methods: {
        toggle() {
            this.expandDrawer = !this.expandDrawer;
        },
        iconColor(navItem) {
            if (navItem.to === this.$route.path) {
                return appColors.navIconActive;
            }
            return appColors.navIcon;
        },
    },
    computed: {
        routeName() {
            return this.$route.meta.pageId || 'no-page-id';
        },
        expandDrawer: {
            get() {
                return this.$store.state.navdrawer.show;
            },
            set(val) {
                this.$store.dispatch(SHOW_NAV_DRAWER, val);
            },
        },
        // returns true if the route is a full-screen map page
        mapRoute() {
            return [
                'watershed',
                'streamflow',
                'climate',
                'groundwaterlevel',
                'groundwaterquality',
                'surfacewaterquality',
            ].includes(this.routeName);
        },
        loading() {
            return this.$store.state.loading.length;
        },
        mapSourceLoaded() {
            return this.$store.state.map.sourceDataLoaded;
        },
        routeHeader() {
            return this.$route.meta.toolHeader || 'BC Water Tool';
        },
    },
    watch: {
        // shrink nav on route change
        $route() {
            this.expandDrawer = false;
        },
    },
};
</script>

<style lang="scss">
// reset overflow to auto (don't show scrollbars all the time)
html {
    overflow-y: auto;
}

// IE11 hand-holding
.layout-main {
    min-height: 100vh;
}

// remove padding and make certain pages full-screen (for map pages)
.page.map-page {
    padding: 0;
    height: 100%;
}

//  manually pad the navigation drawer
//  it's technically just floating, but we'll position it
//  to look like it's part of the main layout
.nav-main {
    padding-top: 64px !important; // height of the toolbar
    max-height: 100vh;
}

.nav-main {
    .nav-icon {
        $svg-icon-size: 2.5rem;
        height: $svg-icon-size;
        width: $svg-icon-size;
    }
}

.ontop {
    z-index:5;
}

// pad the content to display next to the nav
.content-wrap {
    // nav drawer's mini-variant-width value
    padding-left: 80px !important;
}

// map sidebar mobile view
@media (max-width: 600px) {
    .toolbar-route-header {
        display: none;
    }
}

.map-loading-scrim {
    position: absolute;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    background: rgba(0, 0, 0, 0.6);
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .loading-wrap {
        width: 10vw;
    }
}

/* transition styles */
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}

#tourActivator {
    position:fixed;
    bottom:80px;
    left: 5px;
    z-index: 4;
}
// needed to create the hack for the tour overlay.
#navDrawer span.tooltip {
    display:block;
}
</style>



// WEBPACK FOOTER //
// src/App.vue